/**
 * SetPublicPanel — the BUSINESS gate surface (WP-ACP-3, Brief 12;
 * decision-2026-07-24 two-approvals model).
 *
 * Shown inside the service detail overlay when the SET_PUBLIC_GATE feature flag
 * is on. Under that flag the UAT approval (UatDecisionPanel) is the TECHNICAL
 * gate only — it publishes the deployment but leaves the app private. This panel
 * is the SECOND, business approval: a tenant-admin confirms that cost,
 * performance and security responsibility for the app go live for the tenant,
 * and the app becomes public.
 *
 *   POST /api/broker/v1/apps/:app/set-public   (tenant-admin; 409 if not yet
 *   approved, 404 when the flag is off — server-enforced; the SPA guard is UX).
 *
 * Visibility is gated on the flag AND the tenant-admin role AND the app still
 * being private — once public there is nothing left to do here.
 */

import { useState } from 'react';

interface SetPublicTile {
  id: string;
  title: string;
  status: 'public' | 'private' | 'coming-soon' | (string & {});
}

interface SetPublicPanelProps {
  tile: SetPublicTile;
  token: string | null;
  /** config.features.setPublicGate — the business-gate flag (promoteUatGate pattern). */
  setPublicGate: boolean;
  /** UX role guard; the API re-checks tenant-admin server-side. */
  isTenantAdmin: boolean;
  onDone?: () => void;
}

type PanelState =
  | { tag: 'idle' }
  | { tag: 'confirming' }
  | { tag: 'submitting' }
  | { tag: 'done' }
  | { tag: 'error'; message: string };

export default function SetPublicPanel({ tile, token, setPublicGate, isTenantAdmin, onDone }: SetPublicPanelProps) {
  const [state, setState] = useState<PanelState>({ tag: 'idle' });

  // Flag off, not a tenant-admin, or already public → nothing to render.
  if (!setPublicGate || !isTenantAdmin || tile.status === 'public') return null;

  async function confirmSetPublic() {
    if (!token) { setState({ tag: 'error', message: 'not-authenticated' }); return; }
    setState({ tag: 'submitting' });
    try {
      const res = await fetch(
        `/api/broker/v1/apps/${encodeURIComponent(tile.id)}/set-public`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: '{}' },
      );
      if (res.ok) { setState({ tag: 'done' }); onDone?.(); return; }
      const reason = res.status === 403 ? 'forbidden' : res.status === 409 ? 'not-approved' : res.status === 404 ? 'gate-off' : 'failed';
      setState({ tag: 'error', message: reason });
    } catch {
      setState({ tag: 'error', message: 'network' });
    }
  }

  const busy = state.tag === 'submitting';

  return (
    <div
      style={{
        borderWidth: '1px', borderStyle: 'solid',
        borderColor: 'var(--dp-teal-300)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--dp-teal-050)',
        padding: 'var(--sp-4)',
        display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <span
          style={{
            fontSize: 'var(--fs-eyebrow)', fontWeight: 600, letterSpacing: '0.08em',
            textTransform: 'uppercase', padding: '2px 8px', borderRadius: 'var(--radius-sm)',
            background: 'var(--dp-teal-100)', color: 'var(--dp-teal-700)',
          }}
        >
          Business gate
        </span>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', fontWeight: 600, color: 'var(--fg-1)' }}>
          Make {tile.title} public
        </span>
      </div>

      <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-2)', lineHeight: 'var(--lh-body-sm)', margin: 0 }}>
        UAT approval was the technical decision. Setting the app public is the
        separate business decision: it makes the app live for every user on this
        tenant.
      </p>

      {state.tag !== 'done' && state.tag !== 'confirming' && state.tag !== 'submitting' && (
        <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
          <button className="dp-btn dp-btn--primary" onClick={() => setState({ tag: 'confirming' })}>
            Set public
          </button>
        </div>
      )}

      {(state.tag === 'confirming' || state.tag === 'submitting') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }} role="alertdialog" aria-label="Confirm set public">
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-1)', lineHeight: 'var(--lh-body-sm)', margin: 0 }}>
            Confirm the business gate. Setting <strong>{tile.title}</strong> public
            takes on the cost, performance and security responsibility for running
            this app live for the tenant. This is the second of two approvals — the
            UAT approval covered the technical readiness; this one is the business
            decision to go live.
          </p>
          <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
            <button className="dp-btn dp-btn--primary" disabled={busy} onClick={confirmSetPublic}>
              {busy ? 'Setting public…' : 'Confirm — set public'}
            </button>
            <button className="dp-btn dp-btn--ghost" disabled={busy} onClick={() => setState({ tag: 'idle' })}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {state.tag === 'done' && (
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', fontWeight: 600, color: 'var(--dp-teal-700)', margin: 0 }}>
          {tile.title} is now public — live for the tenant.
        </p>
      )}

      {state.tag === 'error' && (
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--dp-crimson-600)', margin: 0 }}>
          {state.message === 'forbidden' ? 'You need the tenant-admin role to set an app public.'
            : state.message === 'not-approved' ? 'This app has no approved (published) deployment yet — approve it in UAT first.'
            : state.message === 'gate-off' ? 'The set-public gate is not enabled here.'
            : 'Could not set the app public. Please try again.'}
        </p>
      )}
    </div>
  );
}
